import { Box, ClickAwayListener, InputAdornment, Tooltip, Typography, useTheme } from '@mui/material';
import Button from 'components/Common/Button';
import Input from 'components/Common/Input';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import BottomLeft from 'assets/BottomLeft.svg';
import BottomCenter from 'assets/BottomCenter.svg';
import BottomRight from 'assets/BottomRight.svg';
import Grid from '@mui/material/Grid2';
import { useGetAgent, useUpdateAgentConfig } from 'apis/business/agent/hook';
import Spinner from 'components/Common/Spinner';
import { Link } from 'react-router-dom';
import * as urls from 'routes/config';
import { WEB_EMBED_SCRIPT } from 'env';
import BrushIcon from 'assets/icons/BrushIcon';
import { MdContentPaste } from 'react-icons/md';
import EyeIcon from 'assets/icons/EyeIcon';

export default function DeployWeb() {
  const theme = useTheme();
  const { data: agent, isLoading } = useGetAgent();
  const [screenPosition, setScreenPosition] = useState<'left' | 'center' | 'right'>('center');
  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
    handleCopy();
  };

  useEffect(() => {
    setScreenPosition(agent?.web_embed_configuration?.screen_position ?? 'center');
  }, [agent]);

  const handleScreenPositionChange = (type: 'left' | 'center' | 'right') => {
    setScreenPosition(type);
  };

  const { handleSubmit, control, watch } = useForm({
    values: {
      agent: {
        embed_url: agent?.embed_url || '',
        web_embed_configuration: agent?.web_embed_configuration || {}
      }
    }
  });

  const { mutateAsync: updateAgent, isPending } = useUpdateAgentConfig();

  const handleCopy = async () => {
    const script = `<script src="${WEB_EMBED_SCRIPT}" id="embed-script"></script>
<script type="text/javascript">
    addEventListener("load", () => {
      VoiceBot?.open(${JSON.stringify({ agentToken: agent?.agent_token })});
    });
</script>`;

    await navigator.clipboard.writeText(script);
  };

  const onSubmit = (data: any) => {
    data.agent.web_embed_configuration.screen_position = screenPosition;
    updateAgent(data);
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)}>
      <Typography variant='h6' fontSize='500' mb={2}>
        Deploy Web
      </Typography>
      <Box bgcolor={theme.palette.background.paper} padding={3} borderRadius={1}>
        <Typography variant='h6' fontSize='500'>
          Deploy agent to website
        </Typography>
        <Typography color='secondary' marginY={2}>
          Insert this script into your &lt;head&gt; tag of your page.
        </Typography>
        <Input
          multiline
          minRows={4}
          name='agent.web_embed_configuration.embed_code'
          label='EMBED CODE'
          value={`<script src="${WEB_EMBED_SCRIPT}" id="embed-script"></script>
<script type="text/javascript">
    addEventListener("load", () => {
      VoiceBot?.open(${JSON.stringify({ agentToken: agent?.agent_token })});
    });
</script>`}
          fullWidth
          disabled
          control={control}
        />
        <ClickAwayListener onClickAway={handleTooltipClose}>
          <Tooltip
            PopperProps={{
              disablePortal: true
            }}
            onClose={handleTooltipClose}
            open={open}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            placement='top'
            title='Copied'
          >
            <Button
              onClick={handleTooltipOpen}
              sx={{
                backgroundColor: theme.palette.primary.light,
                border: 'none',
                marginTop: 3,
                color: theme.palette.primary.main
              }}
            >
              Copy Code
            </Button>
          </Tooltip>
        </ClickAwayListener>
      </Box>
      <Box bgcolor={theme.palette.background.paper} padding={3} borderRadius={1} marginTop={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <BrushIcon />
          <Typography variant='h6' fontSize='500'>
            Customize Styling
          </Typography>
        </Box>
        <Grid container spacing={3} mb={3} mt={1.5}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Input
              name='agent.web_embed_configuration.button_bg_color'
              label='Button Color'
              type='color'
              fullWidth
              control={control}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Input
              name='agent.web_embed_configuration.button_text_color'
              label='Text'
              type='color'
              fullWidth
              control={control}
            />
          </Grid>
        </Grid>
        <Typography variant='h6' fontSize='500'>
          Embed screen position
        </Typography>
        <Typography marginY={2} color='secondary'>
          Adjust the positioning of where the embed renders.
        </Typography>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <Box
              onClick={() => handleScreenPositionChange('left')}
              sx={{
                cursor: 'pointer',
                padding: '13px 19px',
                border: screenPosition === 'left' ? `2px solid ${theme.palette.primary.main}` : '2px solid #F3F3F3'
              }}
              borderRadius={3}
            >
              <Box sx={{ display: 'block', marginX: 'auto' }}>
                <img src={BottomLeft} alt='screen position' />
              </Box>
              <Typography color='secondary' textAlign='center' marginTop={3.5}>
                Bottom left
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <Box
              onClick={() => handleScreenPositionChange('center')}
              sx={{
                cursor: 'pointer',
                border: screenPosition === 'center' ? `2px solid ${theme.palette.primary.main}` : '2px solid #F3F3F3',
                padding: '13px 19px'
              }}
              borderRadius={3}
            >
              <img src={BottomCenter} alt='screen position' />
              <Typography color='secondary' textAlign='center' marginTop={3.5}>
                Bottom Center
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <Box
              onClick={() => handleScreenPositionChange('right')}
              sx={{
                cursor: 'pointer',
                border: screenPosition === 'right' ? `2px solid ${theme.palette.primary.main}` : '2px solid #F3F3F3',
                padding: '13px 19px'
              }}
              borderRadius={3}
            >
              <img src={BottomRight} alt='screen position' />
              <Typography color='secondary' textAlign='center' marginTop={3.5}>
                Bottom Right
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={2.5} marginTop={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Input
              name='agent.web_embed_configuration.screen_bottom_spacing'
              label='Bottom Spacing'
              control={control}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position='end'>px</InputAdornment>
                }
              }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Input
              name='agent.web_embed_configuration.screen_side_spacing'
              label='Side Spacing'
              control={control}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position='end'>px</InputAdornment>
                }
              }}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>

      <Box bgcolor={theme.palette.background.paper} padding={3} borderRadius={1} marginTop={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MdContentPaste style={{ fontSize: 22 }} />
          <Typography variant='h6' fontWeight='500'>
            Customize Content & behavior
          </Typography>
        </Box>
        {/* <Input
          name='agent.web_embed_configuration.button_text'
          label='Button Text'
          control={control}
          fullWidth
          sx={{ marginY: 3}}
        /> */}
        <Input
          multiline
          minRows={5}
          name='agent.web_embed_configuration.help_text'
          label='Text theme (text visible on voicebot header)'
          fullWidth
          control={control}
          sx={{ marginTop: 3 }}
        />
      </Box>

      <Box bgcolor={theme.palette.background.paper} padding={3} borderRadius={1} marginTop={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EyeIcon />
          <Typography variant='h6' fontWeight='500'>
            Preview Your Embed
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 2
          }}
        >
          <Box width='60%'>
            <Input name='agent.embed_url' label='Embed Preview' control={control} fullWidth />
            <Typography fontSize='12px' color='secondary' marginTop={1} lineHeight={1.5}>
              Preview how your assistant will appear on your website (if iframe embedding is allowed). Note: Some
              websites may restrict iframe usage.
            </Typography>
          </Box>
          <Link
            target='_blank'
            rel='noopener noreferrer'
            style={{
              pointerEvents: !watch('agent.embed_url') ? 'none' : 'auto'
            }}
            to={`${urls.publicRoutes.embedPreview}?website=${watch('agent.embed_url')}&agentToken=${agent?.agent_token}&test=${false}`}
          >
            <Button>Preview</Button>
          </Link>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'end' }} marginTop={3}>
        <Button sx={{ borderColor: '#F3F3F3', background: 'white' }} color='secondary' variant='outlined'>
          Cancel
        </Button>
        <Button type='submit' isPending={isPending}>
          Save
        </Button>
      </Box>
    </Box>
  );
}
